import React, { useState, useEffect } from "react";
import { toastError, toastSuccess } from "../../utils";

const AddReturn = ({
  isOpen,
  onClose,
  onSubmit,
  purchaseData,
  returnType = "purchase",
}) => {
  const [selectedItem, setSelectedItem] = useState("");
  const [returnQuantity, setReturnQuantity] = useState("");
  const [returnItems, setReturnItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Get available items (not already selected)
  const getAvailableItems = () => {
    const selectedIds = returnItems.map((item) => item.selectedItemId);
    return purchaseData?.items?.filter(
      (item) =>
        !selectedIds.includes(item._id) &&
        (returnType === "sale"
          ? (item.remainingQty ?? item.sellingQty)
          : item.remainingQty) > 0,
    );
  };

  const addItemToTable = () => {
    // Validate selection
    if (!selectedItem) {
      toastError("Please select an item");
      return;
    }

    if (!returnQuantity || Number(returnQuantity) <= 0) {
      toastError("Please enter valid quantity");
      return;
    }

    const itemDetails = purchaseData?.items?.find(
      (item) => item._id === selectedItem,
    );

    if (!itemDetails) {
      toastError("Item not found");
      return;
    }

    const availableQty =
      returnType === "sale"
        ? (itemDetails.remainingQty ?? itemDetails.sellingQty)
        : itemDetails.remainingQty;

    if (Number(returnQuantity) > availableQty) {
      toastError(`Quantity cannot exceed ${availableQty}`);
      return;
    }

    // Add item to table
    setReturnItems([
      ...returnItems,
      {
        id: Date.now(),
        selectedItemId: selectedItem,
        quantity: returnQuantity,
        selectedItem: itemDetails,
      },
    ]);

    // Reset form
    setSelectedItem("");
    setReturnQuantity("");
  };

  const removeItemFromTable = (itemId) => {
    setReturnItems(returnItems.filter((item) => item.id !== itemId));
  };

  const updateQuantityInTable = (itemId, newQuantity) => {
    const qty = Number(newQuantity);
    const item = returnItems.find((item) => item.id === itemId);

    if (item) {
      const availableQty =
        returnType === "sale"
          ? (item.selectedItem.remainingQty ?? item.selectedItem.sellingQty)
          : item.selectedItem.remainingQty;

      if (qty > availableQty) {
        toastError(`Quantity cannot exceed ${availableQty}`);
        return;
      }

      if (qty <= 0) {
        toastError("Quantity must be greater than 0");
        return;
      }

      setReturnItems(
        returnItems.map((item) =>
          item.id === itemId ? { ...item, quantity: qty } : item,
        ),
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (returnItems.length === 0) {
      newErrors.items = "At least one item is required";
    }

    if (!reason.trim()) {
      newErrors.reason = "Reason is required";
    }

    setErrors(newErrors);

    if (Object.values(newErrors)[0]) {
      toastError(Object.values(newErrors)[0]);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return; // prevent double click
    if (!validateForm()) return;

    try {
      setLoading(true);

      // validate items
      for (const item of returnItems) {
        const availableQty =
          returnType === "sale"
            ? (item.selectedItem.remainingQty ?? item.selectedItem.sellingQty)
            : item.selectedItem.remainingQty;

        if (
          Number(item.quantity) <= 0 ||
          Number(item.quantity) > availableQty
        ) {
          toastError(
            `Invalid quantity for ${
              item.selectedItem.itemName || item.selectedItem.productId?.name
            }`,
          );
          return;
        }
      }

      let payload = {
        reason,

        items:
          returnType === "sale"
            ? returnItems.map((item) => ({
                productId:
                  typeof item.selectedItem.productId === "object"
                    ? item.selectedItem.productId._id
                    : item.selectedItem.productId,
                itemName:
                  item.selectedItem.itemName ||
                  item.selectedItem.productId?.name,
                returnQty: Number(item.quantity),
              }))
            : returnItems.map((item) => {
                const sel = item.selectedItem;

                // Safety check
                if (!sel.batchCode) {
                  throw new Error(
                    `BatchCode missing for item: ${sel.itemName || "Unknown"}`,
                  );
                }

                return {
                  productId:
                    typeof sel.productId === "object"
                      ? sel.productId._id
                      : sel.productId,

                  batchCode: sel.batchCode,
                  unit: sel.unit || "PCS",
                  purchasePrice: Number(sel.purchasePrice) || 0,
                  itemName:
                    sel.itemName || sel.productId?.name || "Unknown Item",
                  quantity: Number(item.quantity),
                };
              }),
      };

      // attach correct parent ID
      if (returnType === "sale") {
        payload.saleId = purchaseData._id;
      } else {
        payload.purchaseId = purchaseData._id;
      }

      if (!payload.items.length) {
        toastError("No items selected");
        return;
      }

      await onSubmit(payload);

      toastSuccess("Return successful");

      // reset
      setReturnItems([]);
      setReason("");
      setSelectedItem("");
      setReturnQuantity("");
      onClose();
    } catch (err) {
      toastError("Return failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* BACKDROP */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
        style={{ zIndex: 1049 }}
        onClick={onClose}
      />

      {/* SIDEBAR */}
      <div
        className="position-fixed top-0 end-0 h-100 bg-white overflow-hidden p-0 width_style"
        style={{ zIndex: 1050, width: "600px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card h-100 rounded-0 mb-1">
          <div className="bg-custom text-white p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <i
                className="bi bi-arrow-left"
                style={{
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  paddingTop: "4px",
                }}
                onClick={onClose}
              ></i>
              <p className="mb-0">
                Return {returnType === "sale" ? "Sale" : "Purchase"} Items
              </p>
            </div>
          </div>
          <div className="card-body py-3 flex-grow-1 overflow-auto">
            <form
              onSubmit={handleSubmit}
              id="returnForm"
              className="flex-grow-1 overflow-auto"
            >
              {/* Single Dropdown Section */}
              <div className="mb-4 p-3 border rounded">
                <label className="form-label fw-bold mb-2">
                  Select Item <span className="text-danger">*</span>
                </label>

                <div className="row g-2">
                  <div className="col-md-7">
                    <select
                      value={selectedItem}
                      onChange={(e) => setSelectedItem(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Choose an item...</option>
                      {getAvailableItems().map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.itemName ||
                            item.productId?.name ||
                            "Unnamed Item"}
                          {" - Available: "}
                          {returnType === "sale"
                            ? (item.remainingQty ?? item.sellingQty)
                            : item.remainingQty}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <input
                      type="number"
                      value={returnQuantity}
                      onChange={(e) => setReturnQuantity(e.target.value)}
                      placeholder="Quantity"
                      min="1"
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-primary text-nowrap w-100"
                      onClick={addItemToTable}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Table showing selected items */}
              {returnItems.length > 0 && (
                <div className="mb-4">
                  <label className="form-label fw-bold mb-2">
                    Selected Items
                  </label>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Item Name</th>
                          <th>Qty</th>
                          <th>Return Qty</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnItems.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>
                              {item.selectedItem.itemName ||
                                item.selectedItem.productId?.name ||
                                "Unnamed Item"}
                            </td>
                            <td>
                              {returnType === "sale"
                                ? (item.selectedItem.remainingQty ??
                                  item.selectedItem.sellingQty)
                                : item.selectedItem.remainingQty}
                            </td>
                            <td>
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) =>
                                  updateQuantityInTable(item.id, e.target.value)
                                }
                                min="1"
                                max={
                                  returnType === "sale"
                                    ? (item.selectedItem.remainingQty ??
                                      item.selectedItem.sellingQty)
                                    : item.selectedItem.remainingQty
                                }
                                className="form-control form-control-sm"
                                style={{ width: "100px" }}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeItemFromTable(item.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {errors.items && (
                    <small className="text-danger">{errors.items}</small>
                  )}
                </div>
              )}

              {/* Reason for Return */}
              <div className="mb-3">
                <label className="form-label">
                  Reason for Return <span className="text-danger">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setErrors((prev) => {
                      const updated = { ...prev };
                      delete updated.reason;
                      return updated;
                    });
                  }}
                  placeholder="Enter reason for return"
                  className={`form-control ${errors.reason ? "border-danger" : ""}`}
                  rows={4}
                />
                {errors.reason && (
                  <small className="text-danger">{errors.reason}</small>
                )}
              </div>
            </form>
          </div>
          <div className="card-footer bg-white border-top d-flex justify-content-between gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm py-1 px-2"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              form="returnForm"
              disabled={loading}
              className="btn btn-primary btn-sm py-1 px-2"
            >
              {loading ? "Submiting..." : "Submit Return"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddReturn;
